import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { useLocalePath } from "@/i18n/LanguageSync";
import { DEFAULT_LANG } from "@/i18n";

const LEGAL_LINKS = [
  { to: "/legal/terms", key: "terms", testId: "legal-link-terms" },
  { to: "/legal/privacy", key: "privacy", testId: "legal-link-privacy" },
  { to: "/legal/security", key: "security", testId: "legal-link-security" },
  { to: "/legal/cookies", key: "cookies", testId: "legal-link-cookies" },
  { to: "/legal/acceptable-use", key: "acceptableUse", testId: "legal-link-aup" },
  { to: "/legal/api-terms", key: "apiTerms", testId: "legal-link-api-terms" },
  { to: "/legal/dpa", key: "dpa", testId: "legal-link-dpa" },
  { to: "/sla", key: "sla", testId: "legal-link-sla" },
];

export default function LegalLayout({ title, updated = "June 2026", testId, titleKey, children }) {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation("legal");
  const lp = useLocalePath();
  const localizedTitle = titleKey ? t(`pages.${titleKey}`, { defaultValue: title }) : title;
  const showTranslationNotice = i18n.language !== DEFAULT_LANG;

  return (
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 mb-3">
                {t("sidebarTitle")}
              </p>
              <nav className="space-y-1 text-[13.5px]">
                {LEGAL_LINKS.map((l) => {
                  const to = lp(l.to);
                  const active = pathname === to || pathname === l.to;
                  return (
                    <Link
                      key={l.to}
                      to={to}
                      data-testid={l.testId}
                      className={`block px-2.5 py-1.5 rounded-md hover:bg-slate-100 ${
                        active ? "bg-slate-100 text-[#0A1128] font-medium" : "text-slate-700"
                      }`}
                    >
                      {t(`pages.${l.key}`)}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>
          <article data-testid={testId} className="lg:col-span-9 max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">AtlasForge Financial</p>
            <h1 className="font-display mt-3 text-[40px] md:text-[52px] leading-[1.05] text-[#0A1128]">{localizedTitle}</h1>
            <p className="text-[13px] text-slate-500 mt-3">{t("lastUpdated", { date: updated })}</p>

            {showTranslationNotice && (
              <div
                data-testid="legal-translation-notice"
                className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
              >
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[12.5px] font-semibold text-amber-900">
                    {t("translationNoticeTitle")}
                  </p>
                  <p className="text-[12.5px] text-amber-800 mt-1 leading-relaxed">
                    {t("translationNotice")}
                  </p>
                </div>
              </div>
            )}

            <div className="af-divider mt-8" />
            <div className="prose-legal mt-8 text-[15px] leading-relaxed text-slate-700 space-y-6">{children}</div>
          </article>
        </div>
      </div>
    </section>
  );
}

export function H2({ children }) {
  return <h2 className="font-display text-[22px] md:text-[24px] text-[#0A1128] mt-10">{children}</h2>;
}

export function H3({ children }) {
  return <h3 className="font-display text-[17px] text-[#0A1128] mt-6">{children}</h3>;
}

export function UL({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1.5 marker:text-slate-400">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}
